import { ref } from "vue";

import type { CustomFormCloseoutGap } from "@/typings/customForm";
import { GENERIC_ERROR_MESSAGE, reportApiFailure } from "@/vuex/interceptors";

// Shared close-out-override flow. Multi-tech completion is emergent (a status
// rollup on the server), so the four crew endpoints answer a blocked completion
// with the same `409 { errors: CustomFormCloseoutGap[] }` the appointment-status
// gate uses, and accept `custom_form_override_reason` to waive it. This module is
// a singleton so a Vuex action — which has no component to host a modal — can
// raise the shared CloseoutOverrideModal, wait for the admin's reason, and retry.

interface CloseoutError {
  response?: { status?: number; data?: { message?: string; errors?: unknown } };
}

const visible = ref(false);
const gaps = ref<CustomFormCloseoutGap[]>([]);
let resolver: ((reason: string | null) => void) | null = null;

function settle(reason: string | null) {
  visible.value = false;
  gaps.value = [];
  const resolve = resolver;
  resolver = null;
  resolve?.(reason);
}

// Raise the shared modal. Resolves with the reason on "Complete anyway", or null
// on cancel. Single-flight: a second prompt cancels any prompt still open.
export function promptOverride(
  nextGaps: CustomFormCloseoutGap[]
): Promise<string | null> {
  if (resolver) resolver(null);
  gaps.value = nextGaps;
  visible.value = true;
  return new Promise<string | null>((resolve) => {
    resolver = resolve;
  });
}

// State + actions the global CloseoutOverrideModal binds to.
export function useCloseoutOverride() {
  return {
    visible,
    gaps,
    confirm: (reason: string) => settle(reason),
    cancel: () => settle(null)
  };
}

// The gate is the only 409 on these endpoints that answers with an `errors`
// array; every other failure answers with `message`. So the shape discriminates
// it — the status code alone does not (mirrors AppointmentStatusDropdown).
export function closeoutGapsFrom(
  error: unknown
): CustomFormCloseoutGap[] | null {
  const response = (error as CloseoutError)?.response;
  if (response?.status !== 409) return null;
  return Array.isArray(response.data?.errors)
    ? (response.data.errors as CustomFormCloseoutGap[])
    : null;
}

// Wrap a request that can be close-out-blocked. `run` issues it (with
// `ignoreError: true` so the 409 body survives the interceptor) and, when given a
// reason, merges `custom_form_override_reason`. On a close-out 409 we raise the
// shared modal and retry with the reason; cancel rethrows so optimistic callers
// revert exactly as they do today. Because every attempt is issued with
// `ignoreError`, a non-close-out failure — on the first attempt OR the retry — is
// re-flashed here (the interceptor is silenced) and rethrown, so the surface never
// reverts without a toast.
export async function withCloseoutOverride<T>(
  run: (overrideReason?: string) => Promise<T>
): Promise<T> {
  try {
    return await run();
  } catch (error) {
    const blockingGaps = closeoutGapsFrom(error);
    if (!blockingGaps) {
      flashNonCloseoutError(error);
      throw error;
    }

    const reason = await promptOverride(blockingGaps);
    if (reason == null) throw error;

    try {
      return await run(reason);
    } catch (retryError) {
      // Do NOT re-prompt: a repeated close-out 409 here means the waiver was
      // refused (e.g. the client offered override but the server denied
      // :override_closeout), not that we should ask again — so flash and rethrow
      // rather than loop.
      flashNonCloseoutError(retryError);
      throw retryError;
    }
  }
}

function flashNonCloseoutError(error: unknown) {
  const message = (error as CloseoutError)?.response?.data?.message;
  if (!message) reportApiFailure(error, "generic_error");
  window.notify.error(message || GENERIC_ERROR_MESSAGE);
}
